import { Button, Clickbox, Divider, Label, Select, Switch } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import HtmlParser from 'html-react-parser';
import _ from 'lodash';
import styled from 'styled-components';
import { ClearDiv, Col10Col2, ColFull } from '../../../components/common';
import { SurveyQuestionListTable } from '../../../components/surveys/SurveyQuestionListTable';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { ISurveyQuestionEditModel, IValidationResult, QuestionType, SourceType } from '../../../models';
import { IQuestionQuery } from '../../../models/IQuestionQuery';

const RevertWidthWrapper = styled.div`
	.row {
		width: revert;
	}
`;
const BatchActionContainer = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;
`;
BatchActionContainer.displayName = 'BatchActionContainer';
const SetAllQuestionTypesText = styled.div`
	display: flex;
	align-items: center;
	margin-left: 50px;
`;
SetAllQuestionTypesText.displayName = 'SetAllQuestionTypesText';

const StyledSurveyQuestionListTable = styled(SurveyQuestionListTable)`
	&.tws-formatted-table {
		overflow: initial;
	}
	overflow: initial;
`;
StyledSurveyQuestionListTable.displayName = 'SurveyQuestionListTable';

export interface IQuestionSectionContentProps {
	questions: ISurveyQuestionEditModel[];
	textLabel: string;
	instructionalText: string;
	manageCustomButtonText: string;
	clickboxText: string;
	includeDimensions: boolean;
	isDemographic?: boolean;
	supportedCultures: string[];
	validationResult?: IValidationResult;
	openModal: (query: IQuestionQuery) => void;
	openCustomModal: (query: IQuestionQuery) => void;
	deleteQuestion: (question: ISurveyQuestionEditModel) => void;
	ToggleQuestionIsRequired: (question: ISurveyQuestionEditModel, isRequired: boolean) => void;
	UpdateAllQuestionTypes: (questions: ISurveyQuestionEditModel[], questionType: QuestionType) => void;
	ToggleAllQuestionsAreRequired?: (isRequired: boolean) => void;
}

interface IQuestionSectionContentState {
	selectedQuestionType?: QuestionType;
	responseRequired: boolean;
}

export class QuestionSectionContent extends React.Component<IQuestionSectionContentProps, IQuestionSectionContentState> {
	constructor(props: IQuestionSectionContentProps) {
		super(props);

		this.openModal = this.openModal.bind(this);
		this.openCustomModal = this.openCustomModal.bind(this);
		const allResponseRequired = _.every(props.questions, (q) => q.isRequired);
		this.state = { selectedQuestionType: undefined, responseRequired: allResponseRequired };
	}

	public static getDerivedStateFromProps(props: IQuestionSectionContentProps) {
		const allResponseRequired = _.every(props.questions, (q) => q.isRequired);

		return { responseRequired: allResponseRequired };
	}

	public getPrimaryContent(textLabel: string, instructionalText: string, manageCustomButtonText: string, buttonClassNameSuffix: string) {
		return (
			<>
				<ColFull>
					<Label size={'md'}>{textLabel}</Label>
				</ColFull>
				<RevertWidthWrapper>
					<Col10Col2
						leftContent={<p>{instructionalText}</p>}
						rightContent={
							<Button className={`tws-button-manage-custom-${buttonClassNameSuffix}`} onClick={() => this.openCustomModal()}>
								{manageCustomButtonText}
							</Button>
						}
					/>
				</RevertWidthWrapper>
			</>
		);
	}

	public getBatchActions() {
		const { responseRequired } = this.state;
		return (
			<>
				<Divider type='dashed' />
				<BatchActionContainer className={'tws-batch-action-container'}>
					<Switch
						text={
							<>
								<b>Select all</b>&nbsp;Response Required&nbsp;
							</>
						}
						checked={responseRequired}
						onChange={(event: any) => this.handleToggleAllQuestionsAreRequired(event.target.checked)}
					/>
					<SetAllQuestionTypesText className='tws-set-all-question-types-text'>
						{HtmlParser(ContentConstants.QUESTION_TYPES_HELP_TEXT)}
					</SetAllQuestionTypesText>
					<Select
						size='md'
						selectSize='lg'
						dropdownSize='md'
						items={Constants.QuestionTypeOptions}
						displayText={ContentConstants.QUESTION_TYPES_DROPDOWN_PLACEHOLDER}
						onApply={(value) => this.handleUpdateAllQuestionTypess(value as unknown as QuestionType)}
						selectedValue={this.state.selectedQuestionType?.toString()}
					/>
				</BatchActionContainer>
				<Divider type='dashed' />
				<ClearDiv clearHeight='20px' />
			</>
		);
	}

	public getSelectedQuestionVariationIds() {
		return this.props.questions
			.filter((q: ISurveyQuestionEditModel) => q.sourceType === SourceType.Library && q.sourceQuestionVariationId !== undefined)
			.map((q: ISurveyQuestionEditModel) => q.sourceQuestionVariationId || 0);
	}

	public getSelectedCustomQuestionIds() {
		return this.props.questions
			.filter((q: ISurveyQuestionEditModel) => q.sourceType === SourceType.Custom)
			.map((q: ISurveyQuestionEditModel) => q.surveyQuestionId || 0);
	}

	public openModal() {
		const { isDemographic } = this.props;
		const query: IQuestionQuery = {
			selectedQuestionVariationIds: this.getSelectedQuestionVariationIds(),
			selectedCustomQuestionIds: this.getSelectedCustomQuestionIds(),
			isDemographic: isDemographic ?? false,
		};
		this.props.openModal(query);
	}

	public openCustomModal() {
		const { isDemographic } = this.props;
		const query: IQuestionQuery = {
			selectedQuestionVariationIds: this.getSelectedQuestionVariationIds(),
			selectedCustomQuestionIds: this.getSelectedCustomQuestionIds(),
			isDemographic: isDemographic ?? false,
		};
		this.props.openCustomModal(query);
	}

	public handleUpdateAllQuestionTypess(questionType: QuestionType) {
		const { questions, UpdateAllQuestionTypes } = this.props;
		this.setState({ selectedQuestionType: questionType });
		UpdateAllQuestionTypes(questions, questionType);
	}

	public handleToggleAllQuestionsAreRequired(isRequired: boolean) {
		const { ToggleAllQuestionsAreRequired } = this.props;
		this.setState({ responseRequired: isRequired });
		if (ToggleAllQuestionsAreRequired) {
			ToggleAllQuestionsAreRequired(isRequired);
		}
	}

	public render() {
		const {
			includeDimensions,
			instructionalText,
			isDemographic,
			manageCustomButtonText,
			clickboxText,
			questions,
			textLabel,
			validationResult,
		} = this.props;
		const buttonClassNameSuffix = includeDimensions ? 'questions' : 'demographic-questions';
		return (
			<>
				{this.getPrimaryContent(textLabel, instructionalText, manageCustomButtonText, buttonClassNameSuffix)}
				<ColFull>
					<ClearDiv clearHeight='20px' />
					<Clickbox size='lg' onClick={() => this.openModal()} className={`tws-button-manage-${buttonClassNameSuffix}`}>
						{clickboxText}
					</Clickbox>
				</ColFull>
				{questions.length > 0 && (
					<>
						{!isDemographic && this.getBatchActions()}
						<StyledSurveyQuestionListTable
							isDemographic={isDemographic}
							includeDimension={includeDimensions}
							questions={questions}
							supportedCultures={this.props.supportedCultures}
							validationResult={validationResult}
							openModal={this.props.openModal}
							openCustomModal={this.props.openCustomModal}
							DeleteSurveyQuestion={this.props.deleteQuestion}
							ToggleQuestionIsRequired={this.props.ToggleQuestionIsRequired}
							UpdateAllQuestionTypes={this.props.UpdateAllQuestionTypes}
						/>
					</>
				)}
			</>
		);
	}
}
